import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
import { getAllServiceOffers } from './servicesData';
import Footer from './components/Footer';

const SEQUENCE_WHEEL_SENSITIVITY = 0.00055;
const UNLOCK_WHEEL_THRESHOLD = 48;

const OFFER_COLORS = [
  '#D4E4BC',
  '#E8C5E8',
  '#B8E0D2',
  '#FFD6E0',
  '#C9E4DE',
  '#FDE2C4',
  '#D4C1EC',
  '#BDE0FE',
  '#FFF1B6',
  '#E2ECE9',
  '#FAD4D4',
  '#C5E4F3',
  '#E8D5B7',
  '#D9E8C4',
];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getOfferScale(index, progress, offerCount) {
  if (index === 0) {
    return 1;
  }

  const slot = progress * (offerCount - 1);
  return clamp(slot - (index - 1), 0, 1);
}

function ServiceOfferTile({ offer, backgroundColor }) {
  return (
    <div
      className="flex h-full min-h-0 w-full flex-col justify-end overflow-hidden pb-10 pl-8 pr-10 md:pb-14 md:pl-12 md:pr-14"
      style={{ backgroundColor }}
    >
      <h2
        className="max-w-full break-words font-mono font-semibold leading-[0.92] tracking-tight text-black"
        style={{
          fontSize: 'clamp(3.5rem, 8vw, 6.5rem)',
          overflowWrap: 'anywhere',
        }}
      >
        {offer.title}
      </h2>
      <p
        className="services-offer-subline mt-6 min-h-0 max-w-full overflow-hidden text-white"
        style={{ fontSize: 'clamp(1.125rem, 2.2vw, 1.5625rem)' }}
      >
        {offer.description}
      </p>
    </div>
  );
}

function ServicesTitleSelection({ word }) {
  return (
    <span className="services-title-selection">
      <span className="services-title-selection__text">{word}</span>
      <span className="services-title-selection__caret" aria-hidden="true">
        <span className="services-title-selection__caret-line" />
        <span className="services-title-selection__caret-handle" />
      </span>
    </span>
  );
}

function ServicesPageTitle({ language }) {
  const isDe = language === 'de';

  return (
    <h1
      className="text-left font-mono leading-none tracking-tight text-black"
      style={{ fontSize: 'clamp(2.75rem, min(12vw, 15vh), 13.125rem)' }}
    >
      <ServicesTitleSelection word={isDe ? 'Was' : 'What'} />
      {' '}
      {isDe ? 'wir bieten' : 'We Offer'}
    </h1>
  );
}

const OFFER_FRAME_STYLE = {
  width: '75vw',
  height: '75vh',
  maxWidth: '1350px',
  maxHeight: 'min(780px, 100%)',
};

function useServicesScrollSequence(reducedMotion) {
  const [sequenceProgress, setSequenceProgress] = useState(0);
  const [isUnlocked, setIsUnlocked] = useState(reducedMotion);
  const unlockAccumulator = useRef(0);
  const progressRef = useRef(0);
  const isUnlockedRef = useRef(reducedMotion);

  useEffect(() => {
    progressRef.current = sequenceProgress;
  }, [sequenceProgress]);

  useEffect(() => {
    isUnlockedRef.current = isUnlocked;
  }, [isUnlocked]);

  const applyScrollLock = useCallback((locked) => {
    document.body.style.overflow = locked ? 'hidden' : '';
    document.documentElement.style.overflow = locked ? 'hidden' : '';
  }, []);

  const handleWheelDelta = useCallback(
    (deltaY) => {
      if (reducedMotion) {
        return;
      }

      if (isUnlockedRef.current) {
        if (deltaY < 0 && window.scrollY <= 1) {
          applyScrollLock(true);
          unlockAccumulator.current = 0;
          isUnlockedRef.current = false;
          setIsUnlocked(false);

          const next = clamp(
            progressRef.current + deltaY * SEQUENCE_WHEEL_SENSITIVITY,
            0,
            1
          );
          progressRef.current = next;
          setSequenceProgress(next);
        }
        return;
      }

      const next = clamp(
        progressRef.current + deltaY * SEQUENCE_WHEEL_SENSITIVITY,
        0,
        1
      );

      if (deltaY > 0 && next >= 1) {
        unlockAccumulator.current += deltaY;
        if (unlockAccumulator.current >= UNLOCK_WHEEL_THRESHOLD) {
          unlockAccumulator.current = 0;
          applyScrollLock(false);
          isUnlockedRef.current = true;
          setIsUnlocked(true);
        }
        progressRef.current = 1;
        setSequenceProgress(1);
        return;
      }

      unlockAccumulator.current = 0;
      progressRef.current = next;
      setSequenceProgress(next);
    },
    [applyScrollLock, reducedMotion]
  );

  useEffect(() => {
    if (reducedMotion) {
      return undefined;
    }

    applyScrollLock(!isUnlocked);

    const onWheel = (event) => {
      const shouldRelock = isUnlockedRef.current && event.deltaY < 0 && window.scrollY <= 1;

      if (isUnlockedRef.current && !shouldRelock) {
        return;
      }

      event.preventDefault();
      handleWheelDelta(event.deltaY);
    };

    let touchStartY = 0;

    const onTouchStart = (event) => {
      touchStartY = event.touches[0]?.clientY ?? 0;
    };

    const onTouchMove = (event) => {
      const touchY = event.touches[0]?.clientY ?? touchStartY;
      const deltaY = touchStartY - touchY;

      if (Math.abs(deltaY) <= 2) {
        return;
      }

      const shouldRelock = isUnlockedRef.current && deltaY < 0 && window.scrollY <= 1;

      if (isUnlockedRef.current && !shouldRelock) {
        return;
      }

      event.preventDefault();
      handleWheelDelta(deltaY * 1.2);
      touchStartY = touchY;
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      applyScrollLock(false);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [applyScrollLock, handleWheelDelta, isUnlocked, reducedMotion]);

  return { sequenceProgress, isUnlocked };
}

const Services = () => {
  const { language } = useLanguage();
  const reducedMotion = usePrefersReducedMotion();
  const offers = getAllServiceOffers(language);
  const pageTitle = language === 'de' ? 'Was wir bieten' : 'What We Offer';
  const { sequenceProgress, isUnlocked } = useServicesScrollSequence(reducedMotion);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#efefef] font-mono text-black">
      <div className="absolute left-8 top-8 z-50">
        <Link
          to="/"
          className="flex items-center gap-2 rounded-[22px] border-none bg-[#F7F4F5] px-[13.2px] py-[4.4px] font-mono text-sm text-black transition-all hover:bg-[#e0e0e0]"
        >
          <ArrowLeft size={20} />
          {language === 'de' ? 'Zurück' : 'Back'}
        </Link>
      </div>

      <section
        className={`bg-[#efefef] ${isUnlocked ? 'relative min-h-screen' : 'fixed inset-0 z-30'}`}
        aria-label={pageTitle}
      >
        <div className="relative flex h-screen flex-col overflow-hidden bg-[#efefef]">
          <div
            className="pointer-events-none relative z-20 shrink-0"
            style={{
              paddingTop: 'clamp(5rem, 9vh, 6.5rem)',
              paddingLeft: 'clamp(2rem, 5vw, 6rem)',
              paddingRight: 'clamp(2rem, 5vw, 6rem)',
              paddingBottom: 'clamp(0.75rem, 2.2vh, 1.5rem)',
            }}
          >
            <ServicesPageTitle language={language} />
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center px-6">
            {reducedMotion ? (
              <div className="flex h-full w-full flex-col items-center gap-10 overflow-y-auto py-6">
                {offers.map((offer, index) => (
                  <div key={offer.title} className="shrink-0" style={OFFER_FRAME_STYLE}>
                    <ServiceOfferTile
                      offer={offer}
                      backgroundColor={OFFER_COLORS[index % OFFER_COLORS.length]}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="relative max-h-full overflow-hidden bg-[#efefef]" style={OFFER_FRAME_STYLE}>
                {offers.map((offer, index) => {
                  const scale = getOfferScale(index, sequenceProgress, offers.length);
                  const contentOpacity =
                    index === 0 ? 1 : clamp((scale - 0.08) / 0.22, 0, 1);

                  if (index > 0 && scale <= 0.001) {
                    return null;
                  }

                  return (
                    <div
                      key={offer.title}
                      className="absolute inset-0 origin-center"
                      style={{
                        transform: `scale(${scale})`,
                        zIndex: index,
                        pointerEvents: scale > 0.5 ? 'auto' : 'none',
                      }}
                      aria-hidden={index > 0 && scale < 0.05}
                    >
                      <div className="h-full w-full" style={{ opacity: contentOpacity }}>
                        <ServiceOfferTile
                          offer={offer}
                          backgroundColor={OFFER_COLORS[index % OFFER_COLORS.length]}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {!isUnlocked && !reducedMotion && (
            <p
              className="pointer-events-none absolute bottom-10 left-0 right-0 text-center font-mono text-xs uppercase tracking-widest opacity-40"
              style={{ opacity: sequenceProgress > 0.98 ? 0.55 : 0.4 }}
            >
              {language === 'de' ? 'Scrollen' : 'Scroll'}
            </p>
          )}
        </div>
      </section>

      {(isUnlocked || reducedMotion) && (
        <div className="relative z-10">
          <Footer />
        </div>
      )}
    </div>
  );
};

export default Services;
